import PropTypes from 'prop-types';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { List, Map } from 'immutable';

import connectOptions, { mergeProps } from '../utils/connectOptions';
import { Action } from '../components/NextAction';
import {
  getUserSettings,
  changeSorting,
  changeFiltering,
  getNextAction,
  deselectAll,
  selectAll,
  deselectAction,
  selectAction,
  toggleBacklog,
  deleteNextActions,
  removeNAItems,
  saveSettings,
  updateSortOrder,
  updateEvent,
  pushNextActions,
} from '../actions/nextAction/main';
import { fetchEventActivities } from '../actions/eventActivities';
import config from '../config';
import { detectBottom } from '../utils/scrollbar';
import { showError, showInformation } from '../utils/MessagePopup';
import debounce from '../utils/debounce';
import deletionCheck from '../utils/deletingPermission';
import { subscribe } from '../decorators/tabChange';
import { isResearcher } from '../utils/checkPermissions';
import { DELETE_FAIL_TEXT, DELETE_SUCCESSFULLY_TEXT } from '../config/constants/common';
import downloadFile from '../utils/downloadFile';
import { usePopupContext } from '../contexts/PopupContext';

const mapToSorts = (settings, ordereds) => {
  const sorts = settings.get('sorts').map(sort => sort.set('order', ordereds[sort.get('si')] || -1));

  return settings.set('sorts', sorts);
};

/**
 * State component for next action page.
 *
 * @param props {Object}.
 * @param props.user {Immutable.Map} Logged user information.
 * @param props.actions {Immutable.List} List of next action item.
 * @param props.selected {Immutable.List} List of selected next action item.
 * @param props.disabled {Boolean} If set to true, will disabled drop down menu.
 * @param props.exportingDisabled {Boolean} If set to true, will disabled exporting button.
 * @param props.settings {Immutable.Map} User's setting of sorting and filtering.
 * @param props.common {Immutable.Map} Common information.
 * @param props.actionConfigs {Immutable.List} Action configuration of drop down list menu.
 */
const NextActionContainer = forwardRef((props, ref) => {
  const {
    user,
    common,
    actions,
    settings,
    activities,
    history,
    getUserSettings,
    changeSorting,
    changeFiltering,
    getNextAction,
    deselectAll,
    selectAll,
    deselectAction,
    selectAction,
    toggleBacklog,
    deleteNextActions,
    removeNAItems,
    saveSettings,
    updateSortOrder,
    pushNextActions,
    fetchEventActivities,
    ...rest
  } = props;

  const { openPopup, closePopup, startPopupLoading } = usePopupContext();
  const gridRef = useRef(null);

  const commonRef = useRef(common);
  const actionsRef = useRef(actions);
  const settingsRef = useRef(settings);

  commonRef.current = common;
  actionsRef.current = actions;
  settingsRef.current = settings;

  const hasAccess = user ? !isResearcher(user.getIn(['roles', 0, 'slug'], null)) : false;

  const showWarningPopup = useCallback(() => {
    showError(openPopup, config.permisionError, () => {
      history.push('/');
      closePopup();
    });
  }, [openPopup, closePopup, history]);

  const handleScroll = useCallback(
    event => {
      const nextPage = commonRef.current.get('nextPage');
      const isFetchingAction = commonRef.current.get('loadingAction');

      if (detectBottom(event.currentTarget) && nextPage > 0 && !isFetchingAction) {
        getNextAction(nextPage);
      }
    },
    [getNextAction],
  );

  useEffect(() => {
    const grid = gridRef.current;

    if (hasAccess) {
      fetchEventActivities();
      getUserSettings();
      getNextAction();
      grid?.addEventListener('scroll', handleScroll);
    } else {
      showWarningPopup();
    }

    return () => {
      grid?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const setGridReference = useCallback(node => {
    gridRef.current = node;
  }, []);

  const handleChangingSort = useCallback(
    (field, direction, included, order) => {
      if (field !== 'Add...') {
        changeSorting(field, direction, included, order);
      }
    },
    [changeSorting],
  );

  const exportNextActions = useCallback(() => {
    const currentActions = actionsRef.current;
    const currentCommon = commonRef.current;
    const exporting = currentCommon.get('exporting');
    const exportingDisabled = currentCommon.get('exportingDisabled');

    if (!exportingDisabled && !exporting) {
      const idList = currentActions.map(action => action.get('id')).toArray();
      const url = `${config.API_BASE_URL}/api/v1/events/next_actions/export`;

      downloadFile({
        url,
        headers: {
          'content-type': 'application/json',
        },
        method: 'post',
        body: {
          params: {
            selected_ids: idList,
          },
        },
      });
    }
  }, []);

  const handleExportingClick = useMemo(() => debounce(exportNextActions), [exportNextActions]);

  const handleSelectAll = useCallback(() => {
    selectAll();
  }, [selectAll]);

  const handleDeselectAll = useCallback(() => {
    deselectAll();
  }, [deselectAll]);

  const applyFiltering = useCallback(
    event => {
      changeFiltering(event.target.name, event.target.checked);
    },
    [changeFiltering],
  );

  const handleFilteringChange = useMemo(() => debounce(applyFiltering), [applyFiltering]);

  const handleRowSelected = useCallback(
    event => {
      if (event.target.checked) {
        selectAction(event.target.value);
      } else {
        deselectAction(event.target.value);
      }
    },
    [selectAction, deselectAction],
  );

  const deleteNextActionsAfterConfirm = useCallback(
    idList => {
      const beforeStart = () => startPopupLoading();
      const afterError = () => showError(openPopup, [DELETE_FAIL_TEXT]);

      const afterSuccess = () => {
        showInformation(openPopup, DELETE_SUCCESSFULLY_TEXT);
        removeNAItems(idList);
      };

      deleteNextActions(idList, beforeStart, afterSuccess, afterError);
    },
    [startPopupLoading, openPopup, removeNAItems, deleteNextActions],
  );

  const handleDeleteNA = useMemo(() => deletionCheck(user, deleteNextActionsAfterConfirm, { openPopup }), [
    user,
    deleteNextActionsAfterConfirm,
    openPopup,
  ]);

  const handleMenuItemClick = useCallback(
    (event, { key, selected }) => {
      const idList = selected.map(item => item.get('id')).toArray();

      if (key === 'deleteSelected') {
        openPopup('DeleteConfirmPopup', {
          delete: () => handleDeleteNA(idList),
          title: 'Delete Next Action',
        });
      } else if (key === 'pushNextActions') {
        const afterSuccess = () => {
          closePopup();
          getNextAction();
        };

        const afterError = () => {
          closePopup();
        };

        openPopup('PushNextActionPopup', {
          idList,
          activities,
          onUpdate: pushNextActions,
          afterSuccess,
          afterError,
        });
      } else if (key === 'exportSelected') {
        const url = `${config.API_BASE_URL}/api/v1/events/next_actions/export`;

        downloadFile({
          url,
          headers: {
            'content-type': 'application/json',
          },
          method: 'post',
          body: {
            params: {
              selected_ids: idList,
            },
          },
        });
      } else if (key === 'flipSelectedToDifferentTeam') {
        const targetIds = selected.map(item => item.get('targetId'));

        openPopup('InternalFlipPopup', { idList: targetIds });
      } else if (key === 'addUpdateBuyerForSelected') {
        openPopup('AddUpdateBuyerPopup', { onSave: () => {} });
      }
    },
    [openPopup, closePopup, getNextAction, activities, pushNextActions, handleDeleteNA],
  );

  const toggleNextActionBacklog = useCallback(
    ({ id, backlogged }) => {
      toggleBacklog(id, !backlogged);
    },
    [toggleBacklog],
  );

  const handleBLClick = useMemo(() => debounce(toggleNextActionBacklog), [toggleNextActionBacklog]);

  const changeOrder = useCallback(
    ordereds => {
      updateSortOrder(ordereds);
      saveSettings(mapToSorts(settingsRef.current, ordereds).toJS());
    },
    [updateSortOrder, saveSettings],
  );

  const handleChangeOrder = useMemo(() => debounce(changeOrder), [changeOrder]);

  const handleTabNotification = useCallback(() => {
    getNextAction();
  }, [getNextAction]);

  useImperativeHandle(ref, () => ({ handleTabNotification }), [handleTabNotification]);

  const userName = `${user.get('firstName', '')} ${user.get('lastName', '')}`;
  const content = hasAccess ? (
    <Action
      {...rest}
      actions={actions}
      common={common}
      onBLClick={handleBLClick}
      onChangeOrder={handleChangeOrder}
      onChangeSort={handleChangingSort}
      onDeselectAll={handleDeselectAll}
      onExportClick={handleExportingClick}
      onFilterChange={handleFilteringChange}
      onMenuSelect={handleMenuItemClick}
      onRowSelected={handleRowSelected}
      onSelectAll={handleSelectAll}
      setGridReference={setGridReference}
      userName={userName}
    />
  ) : null;

  return (
    <>
      <Helmet title="Next Actions" />
      {content}
    </>
  );
});

NextActionContainer.displayName = 'NextActionContainer';

NextActionContainer.propTypes = {
  actionConfigs: PropTypes.instanceOf(List).isRequired,
  actions: PropTypes.instanceOf(List).isRequired,
  activityFilters: PropTypes.instanceOf(Map).isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  disabled: PropTypes.bool.isRequired,
  exportingDisabled: PropTypes.bool.isRequired,
  mailFilters: PropTypes.instanceOf(Map).isRequired,
  selected: PropTypes.instanceOf(List).isRequired,
  settings: PropTypes.instanceOf(Map).isRequired,
  sorts: PropTypes.instanceOf(List).isRequired,
  typeFilters: PropTypes.instanceOf(Map).isRequired,
  userFilters: PropTypes.instanceOf(Map).isRequired,
};

const stateToProps = state => {
  const na = state.nextAction.main;
  const actions = na.get('actions');
  const selected = actions.filter(a => a.get('selected', false));

  return {
    user: state.auth.get('user', null),
    actions,
    selected,
    disabled: selected.size === 0,
    exportingDisabled: actions.size === 0,
    settings: na.get('settings'),
    sorts: na.getIn(['settings', 'sorts'], List()),
    typeFilters: na.getIn(['settings', 'filters', 0], Map()),
    mailFilters: na.getIn(['settings', 'filters', 1], Map()),
    activityFilters: na.getIn(['settings', 'filters', 2], Map()),
    userFilters: na.getIn(['settings', 'filters', 3], Map()),
    common: na.get('common'),
    actionConfigs: config.tables.getIn(['nextaction', 'action']),
    activities: state.eventActivities.getIn(['activities', 'pushNextActionActivities'], List()),
    backloggedActivities: state.eventActivities.getIn(['activities', 'emailTemplateActivities', 'target'], List()),
  };
};

export { NextActionContainer };
export default connect(
  stateToProps,
  {
    getUserSettings,
    changeSorting,
    changeFiltering,
    getNextAction,
    deselectAll,
    selectAll,
    deselectAction,
    selectAction,
    toggleBacklog,
    deleteNextActions,
    removeNAItems,
    saveSettings,
    updateSortOrder,
    updateEvent,
    pushNextActions,
    fetchEventActivities,
  },
  mergeProps,
  connectOptions,
)(subscribe(memo(NextActionContainer), 'onEventChange'));
